import i18n from '@/i18n/index.js'

/**
 * Dates, numbers and money in the person's language and time zone (D-99: formats follow the
 * interface language; D-48: times shown in the person's zone unless a record has its own).
 */
let timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'Europe/Warsaw'

export function setTimeZone(zone) {
  if (zone) timeZone = zone
}

export function getTimeZone() {
  return timeZone
}

const locale = () => (i18n.global.locale.value === 'en' ? 'en-GB' : 'pl-PL')

function toDate(value) {
  if (!value) return null
  if (value instanceof Date) return value
  // A bare date (Y-m-d) is a calendar day, not midnight UTC.
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [y, m, d] = value.split('-').map(Number)
    return new Date(y, m - 1, d, 12)
  }
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

export function formatDate(value, { zone } = {}) {
  const date = toDate(value)
  if (!date) return ''
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  return new Intl.DateTimeFormat(locale(), { day: '2-digit', month: '2-digit', year: 'numeric', ...(dateOnly ? {} : { timeZone: zone || timeZone }) }).format(date)
}

export function formatDateTime(value, { zone } = {}) {
  const date = toDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: zone || timeZone }).format(date)
}

export function formatTime(value, { zone } = {}) {
  const date = toDate(value)
  if (!date) return ''
  return new Intl.DateTimeFormat(locale(), { hour: '2-digit', minute: '2-digit', timeZone: zone || timeZone }).format(date)
}

/** "za 3 dni", "2 godz. temu" … */
export function formatRelative(value, now = new Date()) {
  const date = toDate(value)
  if (!date) return ''
  const diff = (date.getTime() - now.getTime()) / 1000
  const abs = Math.abs(diff)
  const rtf = new Intl.RelativeTimeFormat(locale(), { numeric: 'auto', style: 'short' })
  if (abs < 60) return rtf.format(Math.round(diff), 'second')
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute')
  if (abs < 86400) return rtf.format(Math.round(diff / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(diff / 86400), 'day')
  if (abs < 86400 * 365) return rtf.format(Math.round(diff / (86400 * 30)), 'month')
  return rtf.format(Math.round(diff / (86400 * 365)), 'year')
}

export function formatNumber(value, digits = undefined) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  return new Intl.NumberFormat(locale(), digits === undefined ? {} : { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n)
}

export function formatMoney(value, currency = 'PLN', { compact = false } = {}) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (Number.isNaN(n)) return String(value)
  try {
    return new Intl.NumberFormat(locale(), { style: 'currency', currency: currency || 'PLN', ...(compact ? { notation: 'compact', maximumFractionDigits: 1 } : {}) }).format(n)
  } catch {
    return `${formatNumber(n, 2)} ${currency || ''}`.trim()
  }
}

export function formatPercent(value) {
  if (value === null || value === undefined || value === '') return ''
  return `${formatNumber(value)} %`
}

export function formatBytes(bytes) {
  const n = Number(bytes) || 0
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${formatNumber(n / 1024, 1)} KB`
  if (n < 1024 * 1024 * 1024) return `${formatNumber(n / 1024 / 1024, 1)} MB`
  return `${formatNumber(n / 1024 / 1024 / 1024, 1)} GB`
}

export function formatAddress(address) {
  if (!address || typeof address !== 'object') return ''
  const street = [address.street, address.building].filter(Boolean).join(' ')
  const city = [address.postal_code, address.city].filter(Boolean).join(' ')
  return [street, city, address.region, address.country && address.country !== 'PL' ? address.country : null].filter(Boolean).join(', ')
}

/** Local date-time for <input type="datetime-local"> from an ISO string, in the person's zone. */
export function toLocalInput(value) {
  const date = toDate(value)
  if (!date) return ''
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .formatToParts(date).map((p) => [p.type, p.value]))
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}`
}

/** ISO string (with offset) from a datetime-local value read in the person's zone. */
export function fromLocalInput(value) {
  if (!value) return null
  const [datePart, timePart = '00:00'] = value.split('T')
  const [y, m, d] = datePart.split('-').map(Number)
  const [hh, mm] = timePart.split(':').map(Number)
  // Find the UTC instant whose wall-clock time in `timeZone` equals the input.
  const guess = Date.UTC(y, m - 1, d, hh, mm)
  const offset = zoneOffsetMinutes(new Date(guess))
  let instant = guess - offset * 60000
  const corrected = zoneOffsetMinutes(new Date(instant))
  if (corrected !== offset) instant = guess - corrected * 60000
  return new Date(instant).toISOString()
}

function zoneOffsetMinutes(date) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-US', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })
    .formatToParts(date).map((p) => [p.type, p.value]))
  const asUtc = Date.UTC(Number(parts.year), Number(parts.month) - 1, Number(parts.day), Number(parts.hour), Number(parts.minute), Number(parts.second))
  return Math.round((asUtc - date.getTime()) / 60000)
}

/** Today's date (Y-m-d) in the person's zone. */
export function todayIso(offsetDays = 0) {
  const date = new Date(Date.now() + offsetDays * 86400000)
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(date).map((p) => [p.type, p.value]))
  return `${parts.year}-${parts.month}-${parts.day}`
}
